import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api/client";
import { SERVICE_OPTIONS } from "../constants/incidentOptions";
import { VALID_DIVISIONS } from "../constants/regions";
import { AuthContext } from "../context/AuthContext";

const DEFAULT_CENTER = [32.7767, -96.797];
const DEFAULT_ZOOM = 10;
const INCIDENT_PAGE_SIZE = 200;
const CHART_COLORS = [
  "#2563eb",
  "#16a34a",
  "#f97316",
  "#a855f7",
  "#dc2626",
  "#0ea5e9",
  "#f59e0b",
  "#059669",
  "#6366f1",
  "#7c3aed",
];
const TEXAS_BOUNDS = {
  lat: { min: 25.837377, max: 36.500704 },
  lng: { min: -106.645646, max: -93.508292 },
};
const SOURCE_FALLBACK = "UNSPECIFIED";
const SOURCE_DISPLAY_LABELS = {
  SELF: "Self",
  WAZE: "Waze",
  "SERVICE CALL": "Service Call",
  [SOURCE_FALLBACK]: "Unspecified",
};

function isWithinTexas(latitude, longitude) {
  return (
    latitude >= TEXAS_BOUNDS.lat.min &&
    latitude <= TEXAS_BOUNDS.lat.max &&
    longitude >= TEXAS_BOUNDS.lng.min &&
    longitude <= TEXAS_BOUNDS.lng.max
  );
}

function toTitleCase(value) {
  if (!value) {
    return "Unknown";
  }
  // Convert to uppercase for consistent display
  return value.replace(/-/g, ' ').toUpperCase();
}

function formatDuration(minutes) {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return "0m";
  }

  const totalMinutes = Math.round(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (remainingMinutes > 0 || parts.length === 0) {
    parts.push(`${remainingMinutes}m`);
  }

  return parts.join(" ");
}

function isWithinDateRange(dateValue, startTime, endTime) {
  const hasStart = typeof startTime === "number";
  const hasEnd = typeof endTime === "number";

  if (!hasStart && !hasEnd) {
    return true;
  }

  const timestamp = Date.parse(dateValue);
  if (Number.isNaN(timestamp)) {
    return false;
  }

  let min = hasStart ? startTime : null;
  let max = hasEnd ? endTime : null;
  if (min !== null && max !== null && min > max) {
    const temp = min;
    min = max;
    max = temp;
  }

  if (min !== null && timestamp < min) {
    return false;
  }

  if (max !== null && timestamp > max) {
    return false;
  }

  return true;
}

function getVideoTimestamp(video) {
  if (!video) {
    return null;
  }
  return video.timestamp || video.recordStartUtc || video.recordEndUtc || null;
}

function normalizeSourceValue(value) {
  if (!value) {
    return SOURCE_FALLBACK;
  }

  const trimmed = value.trim().replace(/^'+|'+$/g, "");
  if (!trimmed) {
    return SOURCE_FALLBACK;
  }

  let normalized = trimmed.toUpperCase();
  if (normalized.includes("LEGACY")) {
    normalized = normalized.replace(/LEGACY/gi, "");
  }

  normalized = normalized.replace(/[_\-\s]+/g, " ").trim();
  if (!normalized) {
    return SOURCE_FALLBACK;
  }

  switch (normalized) {
    case "SVC":
      return "SERVICE CALL";
    case "SERVICE CALL":
      return "SERVICE CALL";
    case "SELF":
    case "SELF INITIATED":
    case "MANUAL":
      return "SELF";
    case "WAZE":
      return "WAZE";
    default:
      return normalized;
  }
}

function getSourceLabelFromCanonical(canonical) {
  return SOURCE_DISPLAY_LABELS[canonical] || toTitleCase(canonical || SOURCE_FALLBACK);
}

function getSourceDisplayLabel(value) {
  return getSourceLabelFromCanonical(normalizeSourceValue(value));
}

function MapAutoFit({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!map) {
      return;
    }

    if (!points || points.length === 0) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      return;
    }

    if (points.length === 1) {
      map.setView(points[0], Math.max(DEFAULT_ZOOM, 12));
      return;
    }

    map.fitBounds(points, { padding: [32, 32] });
  }, [map, points]);

  return null;
}

async function fetchAllIncidents() {
  const results = [];
  let page = 1;
  let total = Infinity;

  while (results.length < total) {
    const response = await api.get("/incidents", {
      params: { page, pageSize: INCIDENT_PAGE_SIZE },
    });

    const payload = response.data || {};
    const items = Array.isArray(payload.items) ? payload.items : [];
    const reportedTotal = Number(payload.total);

    if (Number.isFinite(reportedTotal) && reportedTotal >= 0) {
      total = reportedTotal;
    }

    results.push(...items);

    if (items.length < INCIDENT_PAGE_SIZE) {
      break;
    }

    page += 1;
  }

  return results;
}

function Analytics() {
  const { user } = useContext(AuthContext);
  const [incidents, setIncidents] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [mapFilter, setMapFilter] = useState("texas");
  const [selectedRanger, setSelectedRanger] = useState("all");
  const [selectedIncidentType, setSelectedIncidentType] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  
  // Report generation state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [rangerReportDialogOpen, setRangerReportDialogOpen] = useState(false);
  const [reportDateRange, setReportDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split("T")[0], // Jan 1 of current year
    end: new Date().toISOString().split("T")[0], // Today
  });

  // Expense analytics state
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expensesError, setExpensesError] = useState("");
  const [allExpenses, setAllExpenses] = useState([]);
  const [deleteExpenseId, setDeleteExpenseId] = useState(null);
  const [deletingExpense, setDeletingExpense] = useState(false);

  const handleRefresh = useCallback(() => {
    setRefreshKey((key) => key + 1);
  }, []);

  const handleDeleteExpense = useCallback(async () => {
    if (!deleteExpenseId) return;
    setDeletingExpense(true);
    try {
      await api.delete(`/expenses/${deleteExpenseId}`);
      setAllExpenses((prev) => prev.filter((e) => e.id !== deleteExpenseId));
      // Refresh summary after delete
      const summaryRes = await api.get("/expenses/summary").catch(() => ({ data: null }));
      if (summaryRes.data) {
        setExpenseSummary(summaryRes.data);
      }
      setDeleteExpenseId(null);
    } catch (err) {
      console.error("Failed to delete expense:", err);
      setExpensesError("Failed to delete expense. Please try again.");
    } finally {
      setDeletingExpense(false);
    }
  }, [deleteExpenseId]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError("");
      setExpensesLoading(true);
      setExpensesError("");

      try {
        const [incidentItems, videoItems, expenseData, expenseList] = await Promise.all([
          fetchAllIncidents(),
          api.get("/videos").then((res) => (Array.isArray(res.data) ? res.data : [])).catch(() => []),
          api.get("/expenses/summary").then((res) => res.data).catch(() => null),
          api.get("/expenses?limit=500").then((res) => (Array.isArray(res.data) ? res.data : [])).catch(() => []),
        ]);

        if (cancelled) {
          return;
        }

        setIncidents(incidentItems);
        setVideos(videoItems);
        if (expenseData) {
          setExpenseSummary(expenseData);
        }
        setAllExpenses(expenseList);
        setLastUpdated(new Date());
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError("Unable to load analytics data. Please try again later.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setExpensesLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const startDateTime = useMemo(() => {
    if (!dateRange.start) {
      return null;
    }
    const value = Date.parse(`${dateRange.start}T00:00:00Z`);
    return Number.isNaN(value) ? null : value;
  }, [dateRange.start]);

  const endDateTime = useMemo(() => {
    if (!dateRange.end) {
      return null;
    }
    const value = Date.parse(`${dateRange.end}T23:59:59Z`);
    return Number.isNaN(value) ? null : value;
  }, [dateRange.end]);

  const filteredIncidents = useMemo(() => {
    const hasStart = typeof startDateTime === "number";
    const hasEnd = typeof endDateTime === "number";
    if (!hasStart && !hasEnd) {
      return incidents;
    }
    return incidents.filter((incident) =>
      isWithinDateRange(incident.occurredAtUtc, startDateTime, endDateTime)
    );
  }, [incidents, startDateTime, endDateTime]);

  const filteredVideos = useMemo(() => {
    const hasStart = typeof startDateTime === "number";
    const hasEnd = typeof endDateTime === "number";
    if (!hasStart && !hasEnd) {
      return videos;
    }
    return videos.filter((video) => {
      const timestamp = getVideoTimestamp(video);
      return timestamp ? isWithinDateRange(timestamp, startDateTime, endDateTime) : false;
    });
  }, [videos, startDateTime, endDateTime]);

  const rangerOptions = useMemo(() => {
    const set = new Set();
    filteredIncidents.forEach((incident) => {
      const rangerId = (incident.primaryRangerId || "").trim();
      if (!rangerId) {
        return;
      }
      set.add(rangerId.toUpperCase());
    });
    return Array.from(set).sort();
  }, [filteredIncidents]);

  const incidentTypeOptions = useMemo(() => {
    const set = new Map();
    filteredIncidents.forEach((incident) => {
      const raw = (incident.incidentType || "").trim();
      if (!raw) {
        return;
      }
      const key = raw.toLowerCase();
      if (!set.has(key)) {
        set.set(key, { value: raw, label: toTitleCase(raw) });
      }
    });
    return Array.from(set.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredIncidents]);

  const incidentMarkers = useMemo(() => {
    return filteredIncidents
      .filter((incident) => typeof incident.latitude === "number" && typeof incident.longitude === "number")
      .map((incident) => {
        const latitude = incident.latitude;
        const longitude = incident.longitude;
        return {
          id: incident.id,
          cadId: incident.cadId,
          incidentType: incident.incidentType,
          source: getSourceDisplayLabel(incident.source),
          rangerId: (incident.primaryRangerId || "").trim().toUpperCase(),
          occurredAtUtc: incident.occurredAtUtc,
          position: [latitude, longitude],
          isInTexas: isWithinTexas(latitude, longitude),
        };
      });
  }, [filteredIncidents]);

  const filteredIncidentMarkers = useMemo(() => {
    return incidentMarkers.filter((marker) => {
      if (mapFilter === "texas" && !marker.isInTexas) {
        return false;
      }

      if (selectedRanger !== "all" && marker.rangerId !== selectedRanger) {
        return false;
      }

      if (selectedIncidentType !== "all") {
        const markerType = (marker.incidentType || "").trim();
        if (!markerType || markerType.toLowerCase() !== selectedIncidentType.toLowerCase()) {
          return false;
        }
      }
      return true;
    });
  }, [incidentMarkers, mapFilter, selectedRanger, selectedIncidentType]);

  const texasExcludedCount = useMemo(
    () => incidentMarkers.length - incidentMarkers.filter((marker) => marker.isInTexas).length,
    [incidentMarkers]
  );

  const sourcesData = useMemo(() => {
    const counts = new Map();
    filteredIncidents.forEach((incident) => {
      const canonical = normalizeSourceValue(incident.source);
      counts.set(canonical, (counts.get(canonical) || 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([canonical, value]) => ({
        label: getSourceLabelFromCanonical(canonical),
        value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [filteredIncidents]);

  const divisionData = useMemo(() => {
    const total = filteredIncidents.length;
    if (total === 0) return [];
    const counts = new Map();
    filteredIncidents.forEach((incident) => {
      const division = incident.division || "Unknown";
      counts.set(division, (counts.get(division) || 0) + 1);
    });
    // Order by VALID_DIVISIONS first, then any unexpected values, then Unknown last
    const order = [...VALID_DIVISIONS];
    return Array.from(counts.entries())
      .map(([label, count]) => ({
        label,
        count,
        pct: ((count / total) * 100).toFixed(1),
      }))
      .sort((a, b) => {
        if (a.label === "Unknown") return 1;
        if (b.label === "Unknown") return -1;
        const ai = order.indexOf(a.label);
        const bi = order.indexOf(b.label);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return b.count - a.count;
      });
  }, [filteredIncidents]);

  const monthlyTrendData = useMemo(() => {
    const counts = new Map();
    let minTimestamp = null;
    let maxTimestamp = null;

    filteredIncidents.forEach((incident) => {
      if (!incident.occurredAtUtc) {
        return;
      }
      const occurred = new Date(incident.occurredAtUtc);
      if (Number.isNaN(occurred.getTime())) {
        return;
      }
      const key = `${occurred.getUTCFullYear()}-${String(occurred.getUTCMonth() + 1).padStart(2, "0")}`;
      counts.set(key, (counts.get(key) || 0) + 1);

      const timestamp = occurred.getTime();
      if (minTimestamp === null || timestamp < minTimestamp) {
        minTimestamp = timestamp;
      }
      if (maxTimestamp === null || timestamp > maxTimestamp) {
        maxTimestamp = timestamp;
      }
    });

    const getBucketForDate = (date) => {
      const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
      const label = `${date.toLocaleString("en-US", { month: "short" })} ${String(date.getUTCFullYear()).slice(-2)}`;
      return { key, label, value: counts.get(key) || 0 };
    };

    const hasCustomRange = typeof startDateTime === "number" || typeof endDateTime === "number";

    if (!hasCustomRange) {
      const now = new Date();
      const buckets = [];
      for (let i = 11; i >= 0; i -= 1) {
        const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
        buckets.push(getBucketForDate(date));
      }
      return buckets;
    }

    let startDate = typeof startDateTime === "number" ? new Date(startDateTime) : minTimestamp !== null ? new Date(minTimestamp) : null;
    let endDate = typeof endDateTime === "number" ? new Date(endDateTime) : maxTimestamp !== null ? new Date(maxTimestamp) : null;

    if (!startDate && endDate) {
      startDate = new Date(endDate);
    }
    if (!endDate && startDate) {
      endDate = new Date();
    }
    if (!startDate || !endDate) {
      const now = new Date();
      return [getBucketForDate(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)))];
    }

    if (startDate > endDate) {
      const temp = startDate;
      startDate = endDate;
      endDate = temp;
    }

    const startMonth = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1));
    const endMonth = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), 1));
    const buckets = [];
    const cursor = new Date(startMonth);
    let guard = 0;
    while (cursor <= endMonth && guard < 240) {
      buckets.push(getBucketForDate(new Date(cursor)));
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
      guard += 1;
    }

    return buckets;
  }, [filteredIncidents, startDateTime, endDateTime]);

  const rangerData = useMemo(() => {
    const counts = new Map();

    filteredIncidents.forEach((incident) => {
      const id = incident.primaryRangerId;
      if (!id) {
        return;
      }

      const trimmed = id.trim();
      if (!trimmed) {
        return;
      }

      const key = trimmed.toUpperCase();
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredIncidents]);

  const incidentTypeData = useMemo(() => {
    const counts = new Map();
    filteredIncidents.forEach((incident) => {
      const label = toTitleCase(incident.incidentType || "Unspecified");
      counts.set(label, (counts.get(label) || 0) + 1);
    });

    return Array.from(counts.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredIncidents]);

  const dispositionData = useMemo(() => {
    const counts = new Map();

    // First try to get dispositions from videos
    filteredVideos.forEach((video) => {
      if (!Array.isArray(video.dispositions)) {
        return;
      }

      video.dispositions.forEach((disposition) => {
        const trimmed = (disposition || "").trim();
        if (!trimmed) {
          return;
        }
        const label = toTitleCase(trimmed);
        counts.set(label, (counts.get(label) || 0) + 1);
      });
    });

    // Also get services/dispositions from incidents
    filteredIncidents.forEach((incident) => {
      const services = incident.servicesProvided || [];
      if (Array.isArray(services)) {
        services.forEach((service) => {
          const trimmed = (service || "").trim();
          if (!trimmed) {
            return;
          }
          const label = toTitleCase(trimmed);
          counts.set(label, (counts.get(label) || 0) + 1);
        });
      }
    });

    return Array.from(counts.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredVideos, filteredIncidents]);

  const uniqueRangerCount = useMemo(() => {
    const set = new Set();
    filteredIncidents.forEach((incident) => {
      if (incident.primaryRangerId) {
        set.add(incident.primaryRangerId.trim().toUpperCase());
      }
    });
    return set.size;
  }, [filteredIncidents]);

  const { averageByTypeData, averageByRangerData } = useMemo(() => {
    const byType = new Map();
    const byRanger = new Map();

    filteredIncidents.forEach((incident) => {
      if (!incident.occurredAtUtc || !incident.occurredEndUtc) {
        return;
      }

      const start = new Date(incident.occurredAtUtc);
      const end = new Date(incident.occurredEndUtc);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return;
      }

      const diffMinutes = (end.getTime() - start.getTime()) / 60000;
      if (!Number.isFinite(diffMinutes) || diffMinutes <= 0) {
        return;
      }

      const typeLabel = toTitleCase(incident.incidentType || "Unspecified");
      const typeEntry = byType.get(typeLabel) || { total: 0, count: 0 };
      typeEntry.total += diffMinutes;
      typeEntry.count += 1;
      byType.set(typeLabel, typeEntry);

      const rangerId = (incident.primaryRangerId || "").trim();
      if (rangerId) {
        const rangerKey = rangerId.toUpperCase();
        const rangerEntry = byRanger.get(rangerKey) || { total: 0, count: 0 };
        rangerEntry.total += diffMinutes;
        rangerEntry.count += 1;
        byRanger.set(rangerKey, rangerEntry);
      }
    });

    const averageByTypeData = Array.from(byType.entries())
      .map(([label, value]) => ({ label, avgMinutes: value.total / value.count }))
      .filter((item) => Number.isFinite(item.avgMinutes) && item.avgMinutes > 0)
      .sort((a, b) => b.avgMinutes - a.avgMinutes)
      .slice(0, 10);

    const averageByRangerData = Array.from(byRanger.entries())
      .map(([label, value]) => ({ label, avgMinutes: value.total / value.count }))
      .filter((item) => Number.isFinite(item.avgMinutes) && item.avgMinutes > 0)
      .sort((a, b) => b.avgMinutes - a.avgMinutes)
      .slice(0, 10);

    return { averageByTypeData, averageByRangerData };
  }, [filteredIncidents]);

  // Report data calculation
  const reportData = useMemo(() => {
    const startTime = reportDateRange.start ? new Date(reportDateRange.start).getTime() : null;
    const endTime = reportDateRange.end ? new Date(reportDateRange.end + "T23:59:59").getTime() : null;

    // Filter incidents by report date range
    const reportIncidents = incidents.filter((incident) => {
      const timestamp = Date.parse(incident.occurredAtUtc);
      if (Number.isNaN(timestamp)) return false;
      if (startTime && timestamp < startTime) return false;
      if (endTime && timestamp > endTime) return false;
      return true;
    });

    // Count services provided
    const serviceCounts = new Map();
    SERVICE_OPTIONS.forEach((service) => serviceCounts.set(service, 0));

    // Count incident types
    const incidentTypeCounts = {
      accidents: 0,
      assistMotorist: 0,
      assistRanger: 0,
      assistOtherAgency: 0,
      debris: 0,
      other: 0,
    };

    // Count sources
    const sourceCounts = {
      self: 0,
      waze: 0,
      serviceCall: 0,
      other: 0,
    };

    // Calculate total on-scene time
    let totalOnSceneMinutes = 0;
    let incidentsWithDuration = 0;

    reportIncidents.forEach((incident) => {
      // Count incident types - normalize dashes to spaces for matching
      const incidentType = (incident.incidentType || "").toLowerCase().replace(/-/g, ' ').trim();
      if (incidentType.includes("accident")) {
        incidentTypeCounts.accidents++;
      } else if (incidentType.includes("assist motorist")) {
        incidentTypeCounts.assistMotorist++;
      } else if (incidentType.includes("assist other agency") || incidentType.includes("other agency")) {
        incidentTypeCounts.assistOtherAgency++;
      } else if (incidentType.includes("debris")) {
        incidentTypeCounts.debris++;
      } else {
        incidentTypeCounts.other++;
      }

      // Count sources - normalize dashes to spaces for matching
      const source = (incident.source || "").toUpperCase().replace(/-/g, ' ').trim();
      if (source === "SELF" || source.includes("SELF") || source === "MANUAL") {
        sourceCounts.self++;
      } else if (source === "WAZE" || source.includes("WAZE")) {
        sourceCounts.waze++;
      } else if (source === "SERVICE CALL" || source === "SVC" || source.includes("SERVICE")) {
        sourceCounts.serviceCall++;
      } else {
        sourceCounts.other++;
      }

      // Count services provided
      const services = incident.servicesProvided || [];
      if (Array.isArray(services)) {
        services.forEach((service) => {
          const normalized = (service || "").toUpperCase().trim();
          if (serviceCounts.has(normalized)) {
            serviceCounts.set(normalized, serviceCounts.get(normalized) + 1);
          } else {
            // Try to match partial
            for (const [key] of serviceCounts) {
              if (normalized.includes(key) || key.includes(normalized)) {
                serviceCounts.set(key, serviceCounts.get(key) + 1);
                break;
              }
            }
          }
        });
      }

      // Calculate duration
      if (incident.occurredAtUtc && incident.occurredEndUtc) {
        const start = new Date(incident.occurredAtUtc);
        const end = new Date(incident.occurredEndUtc);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
          const diffMinutes = (end.getTime() - start.getTime()) / 60000;
          if (diffMinutes > 0 && diffMinutes < 1440) { // Max 24 hours
            totalOnSceneMinutes += diffMinutes;
            incidentsWithDuration++;
          }
        }
      }
    });

    // Format total on-scene time
    const totalHours = Math.floor(totalOnSceneMinutes / 60);
    const totalMinutes = Math.round(totalOnSceneMinutes % 60);

    return {
      totalIncidents: reportIncidents.length,
      serviceCounts: Object.fromEntries(serviceCounts),
      incidentTypeCounts,
      sourceCounts,
      totalOnSceneTime: {
        hours: totalHours,
        minutes: totalMinutes,
        formatted: totalHours > 0 ? `${totalHours}h ${totalMinutes}m` : `${totalMinutes}m`,
        totalMinutes: Math.round(totalOnSceneMinutes),
      },
      incidentsWithDuration,
      dateRange: {
        start: reportDateRange.start,
        end: reportDateRange.end,
      },
    };
  }, [incidents, reportDateRange]);

  // Top Rangers report data calculation
  const topRangersData = useMemo(() => {
    const startTime = reportDateRange.start ? new Date(reportDateRange.start).getTime() : null;
    const endTime = reportDateRange.end ? new Date(reportDateRange.end + "T23:59:59").getTime() : null;

    // Filter incidents by report date range
    const reportIncidents = incidents.filter((incident) => {
      const timestamp = Date.parse(incident.occurredAtUtc);
      if (Number.isNaN(timestamp)) return false;
      if (startTime && timestamp < startTime) return false;
      if (endTime && timestamp > endTime) return false;
      return true;
    });

    // Group incidents by ranger
    const rangerStats = new Map();

    reportIncidents.forEach((incident) => {
      const rangerId = (incident.primaryRangerId || "").trim().toUpperCase();
      if (!rangerId) return;

      if (!rangerStats.has(rangerId)) {
        rangerStats.set(rangerId, {
          rangerId,
          totalIncidents: 0,
          incidentTypes: {},
          services: {},
          totalOnSceneMinutes: 0,
        });
      }

      const stats = rangerStats.get(rangerId);
      stats.totalIncidents++;

      // Count incident types
      const incidentType = (incident.incidentType || "Other").toLowerCase().replace(/-/g, ' ').trim();
      let typeLabel = "Other";
      if (incidentType.includes("accident")) typeLabel = "Accidents";
      else if (incidentType.includes("assist motorist")) typeLabel = "Assist Motorist";
      else if (incidentType.includes("assist other agency") || incidentType.includes("other agency")) typeLabel = "Assist Other Agency";
      else if (incidentType.includes("debris")) typeLabel = "Debris";
      else if (incidentType.includes("assist ranger")) typeLabel = "Assist Ranger";
      
      stats.incidentTypes[typeLabel] = (stats.incidentTypes[typeLabel] || 0) + 1;

      // Count services
      const services = incident.servicesProvided || [];
      if (Array.isArray(services)) {
        services.forEach((service) => {
          const normalized = (service || "").toUpperCase().trim();
          if (normalized) {
            stats.services[normalized] = (stats.services[normalized] || 0) + 1;
          }
        });
      }

      // Calculate on-scene time
      if (incident.occurredAtUtc && incident.occurredEndUtc) {
        const start = new Date(incident.occurredAtUtc);
        const end = new Date(incident.occurredEndUtc);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
          const diffMinutes = (end.getTime() - start.getTime()) / 60000;
          if (diffMinutes > 0 && diffMinutes < 1440) {
            stats.totalOnSceneMinutes += diffMinutes;
          }
        }
      }
    });

    // Sort by total incidents and get top 3
    const sortedRangers = Array.from(rangerStats.values())
      .sort((a, b) => b.totalIncidents - a.totalIncidents)
      .slice(0, 3);

    // Format on-scene time for each ranger
    sortedRangers.forEach((ranger) => {
      const hours = Math.floor(ranger.totalOnSceneMinutes / 60);
      const minutes = Math.round(ranger.totalOnSceneMinutes % 60);
      ranger.formattedTime = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      
      // Sort incident types by count
      ranger.topIncidentTypes = Object.entries(ranger.incidentTypes)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);
      
      // Sort services by count
      ranger.topServices = Object.entries(ranger.services)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);
    });

    return {
      rangers: sortedRangers,
      dateRange: {
        start: reportDateRange.start,
        end: reportDateRange.end,
      },
    };
  }, [incidents, reportDateRange]);

  // Print report handler
  const handlePrintReport = useCallback(() => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const startDate = reportData.dateRange.start 
      ? new Date(reportData.dateRange.start).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
      : "Start";
    const endDate = reportData.dateRange.end
      ? new Date(reportData.dateRange.end).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
      : "End";
    
    // Generated by info
    const generatedBy = user?.name || user?.rangerId || "Unknown";
    const generatedRangerId = user?.rangerId || "N/A";
    const generatedAt = new Date().toLocaleString("en-US", { 
      month: "long", day: "numeric", year: "numeric", 
      hour: "numeric", minute: "2-digit", hour12: true 
    });

    const serviceRows = [
      { label: "Safety Checks", key: "SAFETY CHECK" },
      { label: "Safety Escorts", key: "SAFETY ESCORT" },
      { label: "Tire Changes", key: "TIRE CHANGE" },
      { label: "Tires Aired Up", key: "AIR TIRE" },
      { label: "Jump Starts", key: "JUMP START" },
      { label: "Minor Repairs", key: "MINOR REPAIR" },
      { label: "Push/Pulls", key: "PUSH/PULL/TOW/RECOVERY" },
      { label: "Debris Removed", key: "DEBRIS REMOVAL" },
      { label: "Fuel Delivery", key: "FUEL DELIVERY" },
      { label: "Bottles of Water", key: "BOTTLED WATER/SNACK" },
      { label: "Courtesy Rides", key: "COURTESY RIDE" },
      { label: "Traffic Control", key: "TRAFFIC CONTROL" },
      { label: "Major Repairs", key: "MAJOR REPAIR" },
      { label: "Blocked Lanes", key: "BLOCKED LANE" },
      { label: "Diagnosis", key: "DIAGNOSIS" },
      { label: "Fire Extinguisher", key: "FIRE EXTINGUISHER" },
      { label: "Translate", key: "TRANSLATE" },
    ].filter(row => reportData.serviceCounts[row.key] > 0);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Highway Rangers Impact Report</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            background: #f5f5f5; 
            padding: 40px;
            color: #333;
          }
          .page-wrapper {
            display: flex;
            gap: 24px;
            max-width: 1100px;
            margin: 0 auto;
          }
          .report-container {
            flex: 1;
            max-width: 700px;
            background: white;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          }
          .sidebar {
            width: 280px;
            flex-shrink: 0;
          }
          .sidebar-section {
            background: white;
            border-radius: 12px;
            padding: 20px;
            box-shadow: 0 2px 12px rgba(0,0,0,0.08);
          }
          .sidebar-title {
            font-size: 14px;
            font-weight: 700;
            color: #1a365d;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 2px solid #8b5cf6;
          }
          .sidebar-card {
            background: #f8fafc;
            border-left: 3px solid #8b5cf6;
            padding: 12px 14px;
            border-radius: 0 8px 8px 0;
            margin-bottom: 12px;
          }
          .sidebar-card:last-child {
            margin-bottom: 0;
          }
          .sidebar-card-title {
            font-weight: 700;
            color: #1a365d;
            text-transform: uppercase;
            font-size: 12px;
            margin-bottom: 6px;
          }
          .sidebar-card-desc {
            color: #64748b;
            font-size: 12px;
            line-height: 1.5;
          }
          .header {
            background: linear-gradient(135deg, #1a365d 0%, #2d4a7c 100%);
            color: white;
            padding: 28px 40px;
            text-align: center;
          }
          .header h1 {
            font-size: 26px;
            margin-bottom: 6px;
            text-transform: uppercase;
            letter-spacing: 2px;
          }
          .header .date-range {
            font-size: 16px;
            opacity: 0.9;
            margin-bottom: 4px;
          }
          .header .subtitle {
            font-size: 22px;
            font-weight: bold;
            color: #ffc107;
            text-transform: uppercase;
          }
          .content {
            padding: 24px 40px 30px 40px;
          }
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 20px;
          }
          .stat-card {
            background: #1a365d;
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .stat-card .count {
            font-size: 24px;
            font-weight: bold;
          }
          .stat-card .label {
            font-size: 14px;
            text-transform: uppercase;
          }
          .total-section {
            background: #ffc107;
            color: #1a365d;
            padding: 14px;
            border-radius: 10px;
            text-align: center;
            margin-bottom: 16px;
          }
          .total-section .total-count {
            font-size: 36px;
            font-weight: bold;
          }
          .total-section .total-label {
            font-size: 16px;
            text-transform: uppercase;
            font-weight: 600;
          }
          .time-section {
            background: #16a34a;
            color: white;
            padding: 14px;
            border-radius: 10px;
            text-align: center;
          }
          .time-section .time-value {
            font-size: 28px;
            font-weight: bold;
          }
          .time-section .time-label {
            font-size: 14px;
            text-transform: uppercase;
          }
          .footer {
            text-align: center;
            padding: 20px;
            background: #f8f9fa;
            border-top: 1px solid #eee;
          }
          .footer .website {
            font-size: 18px;
            font-weight: bold;
            color: #1a365d;
          }
          .footer .generated-by {
            font-size: 11px;
            color: #6b7280;
            margin-top: 12px;
          }
          .section-title {
            font-size: 14px;
            font-weight: bold;
            color: #1a365d;
            margin: 16px 0 8px 0;
            text-transform: uppercase;
            letter-spacing: 1px;
            border-bottom: 2px solid #ffc107;
            padding-bottom: 6px;
          }
          .section-title:first-child {
            margin-top: 0;
          }
          .breakdown-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
            margin-bottom: 12px;
          }
          .sources-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
            margin-bottom: 12px;
          }
          .breakdown-card {
            background: #f1f5f9;
            padding: 10px 14px;
            border-radius: 6px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-left: 4px solid #1a365d;
          }
          .breakdown-card .label {
            font-size: 14px;
            font-weight: 600;
            color: #1a365d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .breakdown-card .count {
            font-size: 18px;
            font-weight: bold;
            color: #1a365d;
          }
          @media print {
            body { padding: 20px; background: white; }
            .page-wrapper { gap: 16px; }
            .report-container { box-shadow: none; }
            .sidebar-section { box-shadow: none; border: 1px solid #e5e7eb; }
          }
        </style>
      </head>
      <body>
        <div class="page-wrapper">
        <div class="report-container">
          <div class="header">
            <h1>Texas Highway Rangers</h1>
            <div class="date-range">${startDate} to ${endDate}</div>
            <div class="subtitle">Impact Report</div>
          </div>
          <div class="content">
            <div class="section-title">Incident Types</div>
            <div class="breakdown-grid">
              <div class="breakdown-card">
                <span class="label">Accidents</span>
                <span class="count">${reportData.incidentTypeCounts.accidents}</span>
              </div>
              <div class="breakdown-card">
                <span class="label">Assist Motorist</span>
                <span class="count">${reportData.incidentTypeCounts.assistMotorist}</span>
              </div>
              <div class="breakdown-card">
                <span class="label">Assist Other Agency</span>
                <span class="count">${reportData.incidentTypeCounts.assistOtherAgency}</span>
              </div>
              <div class="breakdown-card">
                <span class="label">Debris</span>
                <span class="count">${reportData.incidentTypeCounts.debris}</span>
              </div>
              ${reportData.incidentTypeCounts.other > 0 ? `
              <div class="breakdown-card">
                <span class="label">Other</span>
                <span class="count">${reportData.incidentTypeCounts.other}</span>
              </div>
              ` : ""}
            </div>

            <div class="section-title">Sources</div>
            <div class="sources-grid">
              <div class="breakdown-card">
                <span class="label">Self Initiated</span>
                <span class="count">${reportData.sourceCounts.self}</span>
              </div>
              <div class="breakdown-card">
                <span class="label">Waze Initiated</span>
                <span class="count">${reportData.sourceCounts.waze}</span>
              </div>
              <div class="breakdown-card">
                <span class="label">Service Call</span>
                <span class="count">${reportData.sourceCounts.serviceCall}</span>
              </div>
            </div>

            <div class="section-title">Services Provided</div>
            <div class="stats-grid">
              ${serviceRows.map(row => `
                <div class="stat-card">
                  <span class="count">${reportData.serviceCounts[row.key]}</span>
                  <span class="label">${row.label}</span>
                </div>
              `).join("")}
            </div>
            <div class="total-section">
              <div class="total-count">${reportData.totalIncidents}</div>
              <div class="total-label">Total Incidents</div>
            </div>
            <div class="time-section">
              <div class="time-value">${reportData.totalOnSceneTime.formatted}</div>
              <div class="time-label">Total On-Scene Time</div>
            </div>
          </div>
          <div class="footer">
            <div class="website">Learn More: www.highwayrangers.com</div>
            <div class="generated-by">Generated by ${generatedBy} (Ranger ID: ${generatedRangerId}) on ${generatedAt}</div>
          </div>
        </div>
        <div class="sidebar">
          <div class="sidebar-section">
            <div class="sidebar-title">Source Types Explained</div>
            <div class="sidebar-card">
              <div class="sidebar-card-title">Self Initiated</div>
              <div class="sidebar-card-desc">Incidents discovered and initiated by Rangers while checking their assigned routes. These represent proactive responses where Rangers identify motorists in need before any call or Waze alert is received.</div>
            </div>
            <div class="sidebar-card">
              <div class="sidebar-card-title">Waze Initiated</div>
              <div class="sidebar-card-desc">Incidents identified through Waze traffic alerts and community reports. Rangers monitor these alerts to respond quickly to reported hazards, debris, or vehicles on the shoulder.</div>
            </div>
            <div class="sidebar-card">
              <div class="sidebar-card-title">Service Call</div>
              <div class="sidebar-card-desc">Incidents dispatched to Rangers via direct calls from motorists requesting assistance. These are drivers who contact Highway Rangers directly for help with their vehicle or roadside situation.</div>
            </div>
          </div>
          <div class="sidebar-section" style="margin-top: 16px;">
            <div class="sidebar-title">Dispositions Explained</div>
            <div class="sidebar-card">
              <div class="sidebar-card-title">Minor Repair</div>
              <div class="sidebar-card-desc">Quick fixes performed on-scene that allow the motorist to safely continue their journey. Examples include tightening loose parts, reconnecting hoses, or temporary patches.</div>
            </div>
            <div class="sidebar-card">
              <div class="sidebar-card-title">Major Repair</div>
              <div class="sidebar-card-desc">Significant mechanical work that requires extended time or specialized tools. These repairs address more serious issues that would otherwise leave the vehicle inoperable.</div>
            </div>
          </div>
        </div>
        </div>
        <script>
          window.onload = function() { window.print(); };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  }, [reportData, user]);

  // Print Top Rangers report handler
  const handlePrintRangerReport = useCallback(() => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const startDate = topRangersData.dateRange.start 
      ? new Date(topRangersData.dateRange.start).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
      : "Start";
    const endDate = topRangersData.dateRange.end
      ? new Date(topRangersData.dateRange.end).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
      : "End";
    
    const generatedBy = user?.name || user?.rangerId || "Unknown";
    const generatedRangerId = user?.rangerId || "N/A";
    const generatedAt = new Date().toLocaleString("en-US", { 
      month: "long", day: "numeric", year: "numeric", 
      hour: "numeric", minute: "2-digit", hour12: true 
    });

    const serviceLabels = {
      "SAFETY CHECK": "Safety Checks",
      "SAFETY ESCORT": "Safety Escorts",
      "TIRE CHANGE": "Tire Changes",
      "AIR TIRE": "Tires Aired Up",
      "JUMP START": "Jump Starts",
      "MINOR REPAIR": "Minor Repairs",
      "PUSH/PULL/TOW/RECOVERY": "Push/Pulls",
      "DEBRIS REMOVAL": "Debris Removed",
      "FUEL DELIVERY": "Fuel Delivery",
      "BOTTLED WATER/SNACK": "Water/Snacks",
      "COURTESY RIDE": "Courtesy Rides",
      "TRAFFIC CONTROL": "Traffic Control",
      "MAJOR REPAIR": "Major Repairs",
      "BLOCKED LANE": "Blocked Lanes",
      "DIAGNOSIS": "Diagnosis",
      "FIRE EXTINGUISHER": "Fire Extinguisher",
      "TRANSLATE": "Translate",
    };

    const getRankColor = (index) => {
      if (index === 0) return "#ffc107"; // Gold
      if (index === 1) return "#94a3b8"; // Silver
      return "#cd7f32"; // Bronze
    };

    const getRankLabel = (index) => {
      if (index === 0) return "🥇";
      if (index === 1) return "🥈";
      return "🥉";
    };

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Highway Rangers Top Performers Report</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            background: #f5f5f5; 
            padding: 30px;
            color: #333;
          }
          .report-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          }
          .header {
            background: linear-gradient(135deg, #1a365d 0%, #2d4a7c 100%);
            color: white;
            padding: 24px 40px;
            text-align: center;
          }
          .header h1 {
            font-size: 24px;
            margin-bottom: 6px;
            text-transform: uppercase;
            letter-spacing: 2px;
          }
          .header .date-range {
            font-size: 14px;
            opacity: 0.9;
            margin-bottom: 4px;
          }
          .header .subtitle {
            font-size: 20px;
            font-weight: bold;
            color: #ffc107;
            text-transform: uppercase;
          }
          .content {
            padding: 20px 30px 24px 30px;
          }
          .ranger-card {
            background: #f8fafc;
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 16px;
            border-left: 6px solid #1a365d;
          }
          .ranger-header {
            display: flex;
            align-items: center;
            margin-bottom: 12px;
            gap: 12px;
          }
          .rank-badge {
            font-size: 28px;
          }
          .ranger-info {
            flex: 1;
          }
          .ranger-id {
            font-size: 20px;
            font-weight: bold;
            color: #1a365d;
          }
          .ranger-stats {
            display: flex;
            gap: 16px;
            font-size: 13px;
            color: #64748b;
          }
          .ranger-stats span {
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .incident-count {
            background: #1a365d;
            color: white;
            padding: 8px 16px;
            border-radius: 8px;
            text-align: center;
          }
          .incident-count .number {
            font-size: 24px;
            font-weight: bold;
          }
          .incident-count .label {
            font-size: 10px;
            text-transform: uppercase;
          }
          .breakdown-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-top: 12px;
          }
          .breakdown-box {
            background: white;
            border-radius: 8px;
            padding: 10px;
            border: 1px solid #e2e8f0;
          }
          .breakdown-title {
            font-size: 11px;
            font-weight: bold;
            color: #64748b;
            text-transform: uppercase;
            margin-bottom: 6px;
            border-bottom: 2px solid #ffc107;
            padding-bottom: 4px;
          }
          .breakdown-item {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            padding: 3px 0;
            border-bottom: 1px solid #f1f5f9;
          }
          .breakdown-item:last-child {
            border-bottom: none;
          }
          .breakdown-item .name {
            color: #334155;
          }
          .breakdown-item .count {
            font-weight: bold;
            color: #1a365d;
          }
          .footer {
            text-align: center;
            padding: 16px;
            background: #f8f9fa;
            border-top: 1px solid #eee;
          }
          .footer .website {
            font-size: 16px;
            font-weight: bold;
            color: #1a365d;
          }
          .footer .generated-by {
            font-size: 10px;
            color: #6b7280;
            margin-top: 8px;
          }
          @media print {
            body { padding: 0; background: white; }
            .report-container { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="report-container">
          <div class="header">
            <h1>Texas Highway Rangers</h1>
            <div class="date-range">${startDate} to ${endDate}</div>
            <div class="subtitle">Top Performers Report</div>
          </div>
          <div class="content">
            ${topRangersData.rangers.length === 0 ? `
              <div style="text-align: center; padding: 40px; color: #64748b;">
                No ranger data available for this date range.
              </div>
            ` : topRangersData.rangers.map((ranger, index) => `
              <div class="ranger-card" style="border-left-color: ${getRankColor(index)}">
                <div class="ranger-header">
                  <div class="rank-badge">${getRankLabel(index)}</div>
                  <div class="ranger-info">
                    <div class="ranger-id">Ranger ${ranger.rangerId}</div>
                    <div class="ranger-stats">
                      <span>⏱️ ${ranger.formattedTime} on-scene</span>
                    </div>
                  </div>
                  <div class="incident-count">
                    <div class="number">${ranger.totalIncidents}</div>
                    <div class="label">Incidents</div>
                  </div>
                </div>
                <div class="breakdown-section">
                  <div class="breakdown-box">
                    <div class="breakdown-title">Top Incident Types</div>
                    ${ranger.topIncidentTypes.length === 0 ? `
                      <div class="breakdown-item"><span class="name">No data</span></div>
                    ` : ranger.topIncidentTypes.map(([type, count]) => `
                      <div class="breakdown-item">
                        <span class="name">${type}</span>
                        <span class="count">${count}</span>
                      </div>
                    `).join("")}
                  </div>
                  <div class="breakdown-box">
                    <div class="breakdown-title">Top Services Provided</div>
                    ${ranger.topServices.length === 0 ? `
                      <div class="breakdown-item"><span class="name">No data</span></div>
                    ` : ranger.topServices.map(([service, count]) => `
                      <div class="breakdown-item">
                        <span class="name">${serviceLabels[service] || service}</span>
                        <span class="count">${count}</span>
                      </div>
                    `).join("")}
                  </div>
                </div>
              </div>
            `).join("")}
          </div>
          <div class="footer">
            <div class="website">Learn More: www.highwayrangers.com</div>
            <div class="generated-by">Generated by ${generatedBy} (Ranger ID: ${generatedRangerId}) on ${generatedAt}</div>
          </div>
        </div>
        <script>
          window.onload = function() { window.print(); };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  }, [topRangersData, user]);

  const totalIncidents = filteredIncidents.length;
  
  // Calculate total on-scene time for filtered incidents
  const totalOnSceneTime = useMemo(() => {
    let totalMinutes = 0;
    filteredIncidents.forEach((incident) => {
      if (incident.occurredAtUtc && incident.occurredEndUtc) {
        const start = Date.parse(incident.occurredAtUtc);
        const end = Date.parse(incident.occurredEndUtc);
        if (!Number.isNaN(start) && !Number.isNaN(end) && end > start) {
          totalMinutes += (end - start) / 60000;
        }
      }
    });
    const hours = Math.floor(totalMinutes / 60);
    const mins = Math.round(totalMinutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }, [filteredIncidents]);

  return (
    <DashboardLayout
      title={
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
        >
          <Typography variant="h4">Analytics</Typography>
          {lastUpdated && (
            <Typography variant="body2" color="text.secondary">
              Last refreshed {lastUpdated.toLocaleString()}
            </Typography>
          )}
          <Box flexGrow={1} />
          <Button 
            variant="outlined" 
            startIcon={<AssessmentIcon />}
            onClick={() => setReportDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Impact Report
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<AssessmentIcon />}
            onClick={() => setRangerReportDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Top Rangers
          </Button>
          <Button variant="contained" onClick={handleRefresh} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Stack>
      }
    >
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Box>
          <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Box>
                <Typography variant="h6">Date Range</Typography>
                <Typography variant="body2" color="text.secondary">
                  Applies to every chart, map, and summary on this page.
                </Typography>
              </Box>
              <Box flexGrow={1} />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%", maxWidth: 520 }}>
                <TextField
                  size="small"
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={dateRange.start}
                  onChange={(event) =>
                    setDateRange((prev) => ({ ...prev, start: event.target.value }))
                  }
                  sx={{ minWidth: 180 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  value={dateRange.end}
                  onChange={(event) =>
                    setDateRange((prev) => ({ ...prev, end: event.target.value }))
                  }
                  sx={{ minWidth: 180 }}
                />
                <Button
                  variant="text"
                  onClick={() => setDateRange({ start: "", end: "" })}
                  disabled={!dateRange.start && !dateRange.end}
                >
                  Clear Date Range
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Grid container spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                <Typography variant="overline" color="text.secondary">
                  Total Incidents
                </Typography>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  {totalIncidents}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                <Typography variant="overline" color="text.secondary">
                  On-Scene Time
                </Typography>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  {totalOnSceneTime}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                <Typography variant="overline" color="text.secondary">
                  Unique Rangers
                </Typography>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  {uniqueRangerCount}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} sx={{ mb: 2 }}>
              <Typography variant="h6">Incident Map</Typography>
              <Box flexGrow={1} />
              <ToggleButtonGroup
                size="small"
                value={mapFilter}
                exclusive
                onChange={(_, value) => {
                  if (value) {
                    setMapFilter(value);
                  }
                }}
              >
                <ToggleButton value="all">All Incidents</ToggleButton>
                <ToggleButton value="texas">Texas Only</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="map-ranger-filter-label">Ranger</InputLabel>
                <Select
                  labelId="map-ranger-filter-label"
                  value={selectedRanger}
                  label="Ranger"
                  onChange={(event) => setSelectedRanger(event.target.value)}
                >
                  <MenuItem value="all">All Rangers</MenuItem>
                  {rangerOptions.map((ranger) => (
                    <MenuItem key={ranger} value={ranger}>
                      {ranger}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="map-incident-type-filter-label">Incident Type</InputLabel>
                <Select
                  labelId="map-incident-type-filter-label"
                  value={selectedIncidentType}
                  label="Incident Type"
                  onChange={(event) => setSelectedIncidentType(event.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {incidentTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="text"
                onClick={() => {
                  setSelectedRanger("all");
                  setSelectedIncidentType("all");
                  setMapFilter("texas");
                }}
              >
                Clear Map Filters
              </Button>
            </Stack>
            {mapFilter === "all" && texasExcludedCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {texasExcludedCount} incident{texasExcludedCount === 1 ? "" : "s"} fall outside Texas.
              </Typography>
            )}
            {incidentMarkers.length === 0 ? (
              <Box sx={{ py: 8, textAlign: "center" }}>
                <Typography color="text.secondary">No incident coordinates available.</Typography>
              </Box>
            ) : filteredIncidentMarkers.length === 0 ? (
              <Box sx={{ py: 8, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No incidents match the current filter.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: 400, borderRadius: 2, overflow: "hidden" }}>
                <MapContainer
                  center={DEFAULT_CENTER}
                  zoom={DEFAULT_ZOOM}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom
                  preferCanvas
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors"
                  />
                  <MapAutoFit points={filteredIncidentMarkers.map((marker) => marker.position)} />
                  {filteredIncidentMarkers.map((marker, index) => (
                    <CircleMarker
                      key={marker.id}
                      center={marker.position}
                      radius={7}
                      stroke
                      weight={2}
                      color="#111827"
                      fillColor={CHART_COLORS[index % CHART_COLORS.length]}
                      fillOpacity={0.85}
                    >
                      <Popup>
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2">CAD {marker.cadId}</Typography>
                          <Typography variant="body2">Type: {marker.incidentType || "Unknown"}</Typography>
                          <Typography variant="body2">Source: {marker.source || "Unknown"}</Typography>
                          {marker.occurredAtUtc && (
                            <Typography variant="body2">
                              Occurred: {new Date(marker.occurredAtUtc).toLocaleString()}
                            </Typography>
                          )}
                        </Stack>
                      </Popup>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </Box>
            )}
          </Paper>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Incident Totals by Source
                </Typography>
                {sourcesData.length === 0 ? (
                  <Typography color="text.secondary">No source data available.</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={sourcesData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" interval={0} tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip cursor={{ fill: "rgba(148, 163, 184, 0.15)" }} />
                      <Bar dataKey="value" fill="#2563eb" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Incidents per Month (Last 12 Months)
                </Typography>
                {monthlyTrendData.every((item) => item.value === 0) ? (
                  <Typography color="text.secondary">No incident history available.</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={monthlyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip cursor={{ strokeDasharray: "4 2" }} />
                      <Line type="monotone" dataKey="value" stroke="#16a34a" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Incidents by Ranger
                </Typography>
                {rangerData.length === 0 ? (
                  <Typography color="text.secondary">No ranger participation data available.</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={rangerData} margin={{ left: 12, right: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} />
                      <YAxis allowDecimals={false} />
                      <Tooltip cursor={{ fill: "rgba(148, 163, 184, 0.15)" }} />
                      <Bar dataKey="value" fill="#a855f7" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Incident Type Distribution
                </Typography>
                {incidentTypeData.length === 0 ? (
                  <Typography color="text.secondary">No incident type data available.</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Pie
                        data={incidentTypeData}
                        dataKey="value"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        cornerRadius={4}
                      >
                        {incidentTypeData.map((entry, index) => (
                          <Cell key={entry.label} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Avg On-Scene Time by Incident Type
                </Typography>
                {averageByTypeData.length === 0 ? (
                  <Typography color="text.secondary">No on-scene duration data available.</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={averageByTypeData} margin={{ left: 12, right: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} />
                      <YAxis tickFormatter={(value) => formatDuration(value)} allowDecimals={false} />
                      <Tooltip formatter={(value) => [formatDuration(value), "Avg Time"]} />
                      <Bar dataKey="avgMinutes" fill="#0ea5e9" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Avg On-Scene Time by Ranger
                </Typography>
                {averageByRangerData.length === 0 ? (
                  <Typography color="text.secondary">No on-scene duration data available.</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={averageByRangerData} margin={{ left: 12, right: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} />
                      <YAxis tickFormatter={(value) => formatDuration(value)} allowDecimals={false} />
                      <Tooltip formatter={(value) => [formatDuration(value), "Avg Time"]} />
                      <Bar dataKey="avgMinutes" fill="#059669" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Disposition Usage
            </Typography>
            {dispositionData.length === 0 ? (
              <Typography color="text.secondary">No disposition data recorded.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Pie
                    data={dispositionData}
                    dataKey="value"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label
                    labelLine={false}
                  >
                    {dispositionData.map((entry, index) => (
                      <Cell key={entry.label} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>

          {/* Division Breakdown */}
          <Paper sx={{ p: 2.5, borderRadius: 2, mt: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Incidents by Region / Division
            </Typography>
            {divisionData.length === 0 ? (
              <Typography color="text.secondary">No division data available.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Division</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Count</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>% of Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {divisionData.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                        <TableCell align="right">{row.pct}%</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ "& td": { fontWeight: 700, borderTop: 2, borderColor: "divider" } }}>
                      <TableCell>Total</TableCell>
                      <TableCell align="right">{filteredIncidents.length}</TableCell>
                      <TableCell align="right">100%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Expense Analytics Section */}
          <Divider sx={{ my: 4 }} />
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            💰 Expense Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Financial transparency tracking - all expenses logged by Rangers for equipment, supplies, and operational costs.
          </Typography>

          {expensesLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {expensesError && (
            <Alert severity="error" sx={{ mb: 3 }}>{expensesError}</Alert>
          )}

          {!expensesLoading && !expensesError && expenseSummary && (
            <>
              {/* Expense Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, borderRadius: 2, height: "100%", bgcolor: "#ecfdf5" }}>
                    <Typography variant="overline" color="text.secondary">
                      Total Expenses
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1, color: "#059669", fontWeight: 700 }}>
                      ${expenseSummary.totalAmountDollars?.toFixed(2) || "0.00"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                    <Typography variant="overline" color="text.secondary">
                      Total Entries
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {expenseSummary.totalCount || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                    <Typography variant="overline" color="text.secondary">
                      Rangers Contributing
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {expenseSummary.byRanger?.length || 0}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Expense Charts */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Expenses by Category Chart */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Expenses by Category
                    </Typography>
                    {!expenseSummary.byCategory?.length ? (
                      <Typography color="text.secondary">No expense data available.</Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart 
                          data={expenseSummary.byCategory.map(cat => ({
                            label: cat.categoryName?.length > 15 ? cat.categoryName.substring(0, 15) + "..." : cat.categoryName,
                            fullLabel: cat.categoryName,
                            value: cat.totalDollars || 0,
                            count: cat.count,
                          }))} 
                          margin={{ left: 12, right: 12 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={80} />
                          <YAxis tickFormatter={(value) => `$${value}`} allowDecimals={false} />
                          <Tooltip 
                            formatter={(value, name, props) => [`$${value.toFixed(2)} (${props.payload.count} entries)`, props.payload.fullLabel]}
                          />
                          <Bar dataKey="value" fill="#059669" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                </Grid>

                {/* Expenses by Ranger Chart */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Expenses by Ranger
                    </Typography>
                    {!expenseSummary.byRanger?.length ? (
                      <Typography color="text.secondary">No ranger expense data available.</Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart 
                          data={expenseSummary.byRanger.slice(0, 10).map(ranger => ({
                            label: ranger.rangerId || `User ${ranger.userId}`,
                            name: ranger.rangerName || "Unknown",
                            value: ranger.totalDollars || 0,
                            count: ranger.count,
                          }))} 
                          margin={{ left: 12, right: 12 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
                          <YAxis tickFormatter={(value) => `$${value}`} allowDecimals={false} />
                          <Tooltip 
                            formatter={(value, name, props) => [`$${value.toFixed(2)} (${props.payload.count} entries)`, props.payload.name]}
                          />
                          <Bar dataKey="value" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Monthly Expense Trend */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Monthly Expense Trend
                    </Typography>
                    {!expenseSummary.byMonth?.length ? (
                      <Typography color="text.secondary">No monthly expense data available.</Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart 
                          data={expenseSummary.byMonth.slice(-12).map(month => ({
                            label: `${month.monthName?.substring(0, 3)} ${String(month.year).slice(-2)}`,
                            value: month.totalDollars || 0,
                            count: month.count,
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={(value) => `$${value}`} allowDecimals={false} />
                          <Tooltip formatter={(value, name, props) => [`$${value.toFixed(2)} (${props.payload.count} entries)`, "Total"]} />
                          <Line type="monotone" dataKey="value" stroke="#f97316" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                </Grid>

                {/* Category Distribution Pie */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2.5, borderRadius: 2, height: "100%" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Category Distribution
                    </Typography>
                    {!expenseSummary.byCategory?.length ? (
                      <Typography color="text.secondary">No category data available.</Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={320}>
                        <PieChart>
                          <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Pie
                            data={expenseSummary.byCategory.map(cat => ({
                              name: cat.categoryName?.length > 20 ? cat.categoryName.substring(0, 20) + "..." : cat.categoryName,
                              value: cat.totalDollars || 0,
                            }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={90}
                            paddingAngle={2}
                            cornerRadius={4}
                          >
                            {expenseSummary.byCategory.map((entry, index) => (
                              <Cell key={entry.category} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              {/* Detailed Expense Tables */}
              <Grid container spacing={3}>
                {/* By Ranger Table */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Expenses by Ranger (Detail)
                    </Typography>
                    {!expenseSummary.byRanger?.length ? (
                      <Typography color="text.secondary">No ranger data available.</Typography>
                    ) : (
                      <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                              <th style={{ textAlign: "left", padding: "8px", fontSize: "0.875rem" }}>Ranger</th>
                              <th style={{ textAlign: "right", padding: "8px", fontSize: "0.875rem" }}>Entries</th>
                              <th style={{ textAlign: "right", padding: "8px", fontSize: "0.875rem" }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expenseSummary.byRanger.map((ranger) => (
                              <tr key={ranger.userId} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                <td style={{ padding: "8px" }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {ranger.rangerName || "Unknown"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {ranger.rangerId || `User #${ranger.userId}`}
                                  </Typography>
                                </td>
                                <td style={{ textAlign: "right", padding: "8px" }}>{ranger.count}</td>
                                <td style={{ textAlign: "right", padding: "8px", fontWeight: 600, color: "#059669" }}>
                                  ${ranger.totalDollars?.toFixed(2) || "0.00"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* By Category Table */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Expenses by Category (Detail)
                    </Typography>
                    {!expenseSummary.byCategory?.length ? (
                      <Typography color="text.secondary">No category data available.</Typography>
                    ) : (
                      <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                              <th style={{ textAlign: "left", padding: "8px", fontSize: "0.875rem" }}>Category</th>
                              <th style={{ textAlign: "right", padding: "8px", fontSize: "0.875rem" }}>Entries</th>
                              <th style={{ textAlign: "right", padding: "8px", fontSize: "0.875rem" }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expenseSummary.byCategory.map((cat) => (
                              <tr key={cat.category} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                <td style={{ padding: "8px" }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {cat.categoryName}
                                  </Typography>
                                </td>
                                <td style={{ textAlign: "right", padding: "8px" }}>{cat.count}</td>
                                <td style={{ textAlign: "right", padding: "8px", fontWeight: 600, color: "#059669" }}>
                                  ${cat.totalDollars?.toFixed(2) || "0.00"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              {/* Admin: All Expenses List */}
              {user?.role?.toLowerCase() === "admin" && allExpenses.length > 0 && (
                <Paper sx={{ p: 2.5, borderRadius: 2, mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    All Expenses (Admin)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    As an admin, you can delete expense entries if needed.
                  </Typography>
                  <Box sx={{ maxHeight: 500, overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: "2px solid #e5e7eb", position: "sticky", top: 0, background: "#fff" }}>
                          <th style={{ textAlign: "left", padding: "8px", fontSize: "0.875rem" }}>Date</th>
                          <th style={{ textAlign: "left", padding: "8px", fontSize: "0.875rem" }}>Ranger</th>
                          <th style={{ textAlign: "left", padding: "8px", fontSize: "0.875rem" }}>Category</th>
                          <th style={{ textAlign: "left", padding: "8px", fontSize: "0.875rem" }}>Description</th>
                          <th style={{ textAlign: "right", padding: "8px", fontSize: "0.875rem" }}>Amount</th>
                          <th style={{ textAlign: "center", padding: "8px", fontSize: "0.875rem" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allExpenses.map((expense) => (
                          <tr key={expense.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ padding: "8px" }}>
                              <Typography variant="body2">
                                {expense.purchaseDate ? new Date(expense.purchaseDate).toLocaleDateString() : "-"}
                              </Typography>
                            </td>
                            <td style={{ padding: "8px" }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {expense.rangerId || expense.rangerName || `User #${expense.userId}`}
                              </Typography>
                            </td>
                            <td style={{ padding: "8px" }}>
                              <Typography variant="body2">
                                {expense.categoryName || `Category ${expense.category}`}
                              </Typography>
                            </td>
                            <td style={{ padding: "8px" }}>
                              <Typography variant="body2" sx={{ maxWidth: 300 }} noWrap title={expense.itemDescription}>
                                {expense.itemDescription || "-"}
                              </Typography>
                            </td>
                            <td style={{ textAlign: "right", padding: "8px", fontWeight: 600, color: "#059669" }}>
                              ${((expense.amountCents || 0) / 100).toFixed(2)}
                            </td>
                            <td style={{ textAlign: "center", padding: "4px" }}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteExpenseId(expense.id)}
                                title="Delete expense"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Paper>
              )}
            </>
          )}

          {!expensesLoading && !expensesError && !expenseSummary?.totalCount && (
            <Alert severity="info">No expenses have been logged yet. Rangers can log expenses from the Resources page or Mobile Dashboard.</Alert>
          )}
        </Box>
      )}

      {/* Delete Expense Confirmation Dialog */}
      <Dialog
        open={!!deleteExpenseId}
        onClose={() => !deletingExpense && setDeleteExpenseId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>Delete Expense?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this expense entry? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteExpenseId(null)} disabled={deletingExpense}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteExpense}
            color="error"
            variant="contained"
            disabled={deletingExpense}
          >
            {deletingExpense ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Generation Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={() => setReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "#1a365d", color: "white" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AssessmentIcon />
            <Typography variant="h6">Generate Impact Report</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={3}>
            {/* Date Range Selection */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Report Date Range
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={reportDateRange.start}
                  onChange={(e) => setReportDateRange(prev => ({ ...prev, start: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={reportDateRange.end}
                  onChange={(e) => setReportDateRange(prev => ({ ...prev, end: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            </Box>

            <Divider />

            {/* Report Preview */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Report Preview
              </Typography>
              
              {/* Summary Stats */}
              <Paper sx={{ p: 3, bgcolor: "#f8fafc", borderRadius: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                      <Chip 
                        label={`${reportData.totalIncidents} Total Incidents`} 
                        color="primary" 
                        sx={{ fontSize: "1.1rem", fontWeight: "bold", py: 2.5, px: 2 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      textAlign: "center", 
                      bgcolor: "#16a34a", 
                      color: "white", 
                      borderRadius: 2, 
                      p: 2,
                      mb: 2
                    }}>
                      <Typography variant="h4" fontWeight="bold">
                        {reportData.totalOnSceneTime.formatted}
                      </Typography>
                      <Typography variant="body2">Total On-Scene Time</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Incident Types Breakdown */}
                <Typography variant="subtitle2" sx={{ mb: 1.5, mt: 2, borderBottom: "2px solid #ffc107", pb: 0.5 }}>
                  Incident Types
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {[
                    { label: "Accidents", value: reportData.incidentTypeCounts.accidents },
                    { label: "Assist Motorist", value: reportData.incidentTypeCounts.assistMotorist },
                    { label: "Assist Other Agency", value: reportData.incidentTypeCounts.assistOtherAgency },
                    { label: "Debris", value: reportData.incidentTypeCounts.debris },
                  ].map((item) => (
                    <Grid item xs={6} sm={4} key={item.label}>
                      <Paper 
                        sx={{ 
                          p: 1.5, 
                          textAlign: "center",
                          bgcolor: item.value > 0 ? "#0ea5e9" : "#e5e7eb",
                          color: item.value > 0 ? "white" : "#6b7280",
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold">
                          {item.value}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                          {item.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {/* Sources Breakdown */}
                <Typography variant="subtitle2" sx={{ mb: 1.5, borderBottom: "2px solid #ffc107", pb: 0.5 }}>
                  Sources
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {/* Left side - Source counts */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      {[
                        { label: "Self Initiated", value: reportData.sourceCounts.self },
                        { label: "Waze Initiated", value: reportData.sourceCounts.waze },
                        { label: "Service Call", value: reportData.sourceCounts.serviceCall },
                      ].map((item) => (
                        <Paper 
                          key={item.label}
                          sx={{ 
                            p: 1.5, 
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            bgcolor: item.value > 0 ? "#8b5cf6" : "#e5e7eb",
                            color: item.value > 0 ? "white" : "#6b7280",
                          }}
                        >
                          <Typography variant="body2" fontWeight="600" sx={{ textTransform: "uppercase" }}>
                            {item.label}
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {item.value}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Grid>
                  {/* Right side - Explanations */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Paper sx={{ p: 1.5, bgcolor: "#f8fafc", borderLeft: "3px solid #8b5cf6" }}>
                        <Typography variant="caption" fontWeight="700" sx={{ color: "#1a365d", textTransform: "uppercase" }}>
                          Self Initiated
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", color: "#64748b", fontSize: "0.7rem" }}>
                          Incidents discovered and initiated by Rangers while patrolling their assigned routes.
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 1.5, bgcolor: "#f8fafc", borderLeft: "3px solid #8b5cf6" }}>
                        <Typography variant="caption" fontWeight="700" sx={{ color: "#1a365d", textTransform: "uppercase" }}>
                          Waze Initiated
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", color: "#64748b", fontSize: "0.7rem" }}>
                          Incidents identified through Waze traffic alerts and responded to by Rangers.
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 1.5, bgcolor: "#f8fafc", borderLeft: "3px solid #8b5cf6" }}>
                        <Typography variant="caption" fontWeight="700" sx={{ color: "#1a365d", textTransform: "uppercase" }}>
                          Service Call
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", color: "#64748b", fontSize: "0.7rem" }}>
                          Incidents dispatched to Rangers via 911 calls, TMC requests, or other agency coordination.
                        </Typography>
                      </Paper>
                    </Stack>
                  </Grid>
                </Grid>

                {/* Service Breakdown */}
                <Typography variant="subtitle2" sx={{ mb: 1.5, borderBottom: "2px solid #ffc107", pb: 0.5 }}>
                  Services Provided
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { label: "Safety Checks", key: "SAFETY CHECK" },
                    { label: "Safety Escorts", key: "SAFETY ESCORT" },
                    { label: "Tire Changes", key: "TIRE CHANGE" },
                    { label: "Tires Aired Up", key: "AIR TIRE" },
                    { label: "Jump Starts", key: "JUMP START" },
                    { label: "Minor Repairs", key: "MINOR REPAIR" },
                    { label: "Push/Pulls", key: "PUSH/PULL/TOW/RECOVERY" },
                    { label: "Debris Removed", key: "DEBRIS REMOVAL" },
                    { label: "Fuel Delivery", key: "FUEL DELIVERY" },
                    { label: "Bottles of Water", key: "BOTTLED WATER/SNACK" },
                    { label: "Courtesy Rides", key: "COURTESY RIDE" },
                    { label: "Traffic Control", key: "TRAFFIC CONTROL" },
                    { label: "Major Repairs", key: "MAJOR REPAIR" },
                  ].map((service) => (
                    <Grid item xs={6} sm={4} key={service.key}>
                      <Paper 
                        sx={{ 
                          p: 1.5, 
                          textAlign: "center",
                          bgcolor: reportData.serviceCounts[service.key] > 0 ? "#1a365d" : "#e5e7eb",
                          color: reportData.serviceCounts[service.key] > 0 ? "white" : "#6b7280",
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold">
                          {reportData.serviceCounts[service.key] || 0}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                          {service.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#f8fafc" }}>
          <Button onClick={() => setReportDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />}
            onClick={handlePrintReport}
            sx={{ bgcolor: "#1a365d" }}
          >
            Print Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Top Rangers Report Dialog */}
      <Dialog 
        open={rangerReportDialogOpen} 
        onClose={() => setRangerReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AssessmentIcon />
            <Typography variant="h6">Top Rangers Report</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, bgcolor: "background.paper" }}>
          <Stack spacing={3}>
            {/* Date Range Selection */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Report Date Range
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={reportDateRange.start}
                  onChange={(e) => setReportDateRange(prev => ({ ...prev, start: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={reportDateRange.end}
                  onChange={(e) => setReportDateRange(prev => ({ ...prev, end: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            </Box>

            <Divider />

            {/* Top Rangers Preview */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Top 3 Rangers by Incident Volume
              </Typography>
              
              {topRangersData.rangers.length === 0 ? (
                <Alert severity="info">No ranger data available for this date range.</Alert>
              ) : (
                <Stack spacing={2}>
                  {topRangersData.rangers.map((ranger, index) => (
                    <Paper 
                      key={ranger.rangerId}
                      variant="outlined"
                      sx={{ 
                        p: 2, 
                        bgcolor: "action.hover", 
                        borderLeft: 6, 
                        borderColor: index === 0 ? "warning.main" : index === 1 ? "grey.400" : "#cd7f32"
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="h4">
                          {index === 0 ? "🥇" : index === 1 ? "🥈" : "🥉"}
                        </Typography>
                        <Box flex={1}>
                          <Typography variant="h6" fontWeight="bold" color="text.primary">
                            Ranger {ranger.rangerId}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ⏱️ {ranger.formattedTime} on-scene
                          </Typography>
                        </Box>
                        <Chip 
                          label={`${ranger.totalIncidents} Incidents`}
                          color="primary"
                          sx={{ fontWeight: "bold" }}
                        />
                      </Stack>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">
                            TOP INCIDENT TYPES
                          </Typography>
                          {ranger.topIncidentTypes.slice(0, 3).map(([type, count]) => (
                            <Stack key={type} direction="row" justifyContent="space-between">
                              <Typography variant="body2">{type}</Typography>
                              <Typography variant="body2" fontWeight="bold">{count}</Typography>
                            </Stack>
                          ))}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">
                            TOP SERVICES
                          </Typography>
                          {ranger.topServices.slice(0, 3).map(([service, count]) => (
                            <Stack key={service} direction="row" justifyContent="space-between">
                              <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                {service.split(" ").map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(" ")}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">{count}</Typography>
                            </Stack>
                          ))}
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "background.default" }}>
          <Button onClick={() => setRangerReportDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />}
            onClick={handlePrintRangerReport}
            disabled={topRangersData.rangers.length === 0}
          >
            Print Report
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Analytics;
